import React, { useState, useEffect } from 'react'
import {
  Calendar, Users, Upload, Settings, Save, AlertCircle,
  CheckCircle, Download, Edit2, Trash2, Plus, FileText,
  ArrowRight, Clock, Home, School
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO, addDays, startOfWeek, endOfWeek } from 'date-fns'
import { useCustodyEligibleChildren } from '@/hooks/useChildren'
import { useFeature } from '@/contexts/FeaturesContext'
import { useQueryClient } from '@tanstack/react-query'
import { parseSettingList, custodyParentsKey } from '@/hooks/useCustodyParents'

interface CustodySchedule {
  date: string
  kids_present: string[]
  day_type: string
  notes?: string
  modifications?: CustodyModification[]
}

interface CustodyModification {
  id: string
  date: string
  child_name: string
  original_parent: string
  actual_parent: string
  reason: string
  agreed_by_both: boolean
  created_by: string
  created_at: string
  notes?: string
}

interface CustodySettings {
  custody_enabled: boolean
  custody_parents: string[]
  custody_children: string[]
  default_schedule: any
  holiday_schedule: any
}

export const CustodyManagement: React.FC = () => {
  const custodyEnabled = useFeature('custody_schedule');
  const { children: childrenData, loading: childrenLoading } = useCustodyEligibleChildren()
  const queryClient = useQueryClient()
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState<'schedule' | 'modifications' | 'settings' | 'import'>('schedule')
  const [schedules, setSchedules] = useState<CustodySchedule[]>([])
  const [modifications, setModifications] = useState<CustodyModification[]>([])
  const [settings, setSettings] = useState<CustodySettings>({
    custody_enabled: true,
    // Two empty slots: the household parent first, then the other parent.
    custody_parents: ['', ''],
    custody_children: [],
    default_schedule: {},
    holiday_schedule: {}
  })
  const [loading, setLoading] = useState(true)  // Start with loading true
  const [error, setError] = useState<string | null>(null)
  const [selectedWeek, setSelectedWeek] = useState(new Date())
  const [editingDay, setEditingDay] = useState<string | null>(null)
  const [editingKids, setEditingKids] = useState<string[]>([])
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  // Checked inside the effect rather than by an early return above it: an
  // early return before a hook changes the hook order once useFeature
  // resolves, which React rejects at runtime.
  useEffect(() => {
    if (!custodyEnabled) return
    loadCustodyData()
  }, [selectedWeek, custodyEnabled])

  useEffect(() => {
    if (!custodyEnabled) return
    loadSettings()
  }, [custodyEnabled])

  useEffect(() => {
    // A family that never chose its custody children starts from its own
    // kids. A saved list is never overwritten.
    if (!settingsLoaded || childrenLoading || !childrenData || childrenData.length === 0) return
    setSettings(prev =>
      prev.custody_children.length > 0
        ? prev
        : { ...prev, custody_children: childrenData.map(child => child.username) }
    )
  }, [childrenData, childrenLoading, settingsLoaded])

  // Hide component if custody feature is disabled. Must stay below every hook.
  if (!custodyEnabled) {
    return null;
  }

  const loadCustodyData = async () => {
    setLoading(true)
    setError(null)
    try {
      const weekStart = startOfWeek(selectedWeek)
      const weekEnd = endOfWeek(selectedWeek)

      // Load schedule
      const scheduleResponse = await apiClient.get('/api/schedule/family-schedule', {
        params: {
          start_date: format(weekStart, 'yyyy-MM-dd'),
          end_date: format(weekEnd, 'yyyy-MM-dd')
        }
      })

      // Load modifications
      const modResponse = await apiClient.get('/api/custody/modifications', {
        params: {
          start_date: format(weekStart, 'yyyy-MM-dd'),
          end_date: format(weekEnd, 'yyyy-MM-dd')
        }
      })

      setSchedules(scheduleResponse.daily_schedules || [])
      setModifications(modResponse || [])
    } catch (error: any) {
      console.error('Failed to load custody data:', error)
      setError(error.message || 'Failed to load custody data')
      toast.error('Failed to load custody data')
    } finally {
      setLoading(false)
    }
  }

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/api/settings')
      const parents = parseSettingList(response?.custody_parents)
      setSettings({
        custody_enabled: response?.custody_features_enabled || true,
        custody_parents: parents.length >= 2 ? parents : [...parents, '', ''].slice(0, 2),
        custody_children: parseSettingList(response?.custody_children),
        default_schedule: response?.custody_default_schedule || {},
        holiday_schedule: response?.custody_holiday_schedule || {}
      })
    } catch (error) {
      console.error('Failed to load custody settings:', error)
    } finally {
      setSettingsLoaded(true)
    }
  }

  const handleSaveSettings = async () => {
    // Stored as JSON lists. Fewer than two parents means "not configured" and
    // the schedule falls back to neutral Parent A / Parent B labels.
    const parents = settings.custody_parents.map(p => p.trim()).filter(Boolean)
    const children = settings.custody_children.map(c => c.trim()).filter(Boolean)
    if (parents.length === 1) {
      toast.error('Enter both parent names, or leave both empty')
      return
    }
    try {
      await apiClient.put('/api/settings/custody_features_enabled', { value: settings.custody_enabled })
      await apiClient.put('/api/settings/custody_parents', { value: parents })
      await apiClient.put('/api/settings/custody_children', { value: children })
      await apiClient.put('/api/settings/custody_default_schedule', { value: settings.default_schedule })
      await apiClient.put('/api/settings/custody_holiday_schedule', { value: settings.holiday_schedule })

      queryClient.invalidateQueries({ queryKey: custodyParentsKey })
      toast.success('Custody settings saved successfully')
    } catch (error: any) {
      console.error('Failed to save custody settings:', error)
      toast.error(error.response?.data?.error || 'Failed to save custody settings')
    }
  }

  const handleUpdateSchedule = async (date: string, kids_present: string[]) => {
    try {
      await apiClient.put(`/api/schedule/family-schedule/${date}`, {
        kids_present,
        notes: `Updated via admin interface at ${new Date().toISOString()}`
      })
      toast.success('Schedule updated successfully')
      loadCustodyData()
      setEditingDay(null)
      setEditingKids([])
    } catch (error) {
      toast.error('Failed to update schedule')
    }
  }

  const handleApproveModification = async (modId: string, approved: boolean) => {
    try {
      await apiClient.put(`/api/custody/modification/${modId}/approve`, {
        approved,
        notes: approved ? 'Approved by admin' : 'Rejected by admin'
      })
      toast.success(approved ? 'Modification approved' : 'Modification rejected')
      loadCustodyData()
    } catch (error) {
      toast.error('Failed to process modification')
    }
  }

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file first')
      return
    }

    const formData = new FormData()
    formData.append('file', uploadFile)

    try {
      const response = await apiClient.post('/api/admin/custody/import-court-order', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toast.success('Court order imported successfully')
      setUploadFile(null)
      loadCustodyData()
    } catch (error) {
      toast.error('Failed to import court order')
    }
  }

  const renderScheduleTab = () => (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">Custody Schedule</h3>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setSelectedWeek(addDays(selectedWeek, -7))}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Previous Week
          </button>

          <div className="text-center">
            <h4 className="font-medium">
              {format(startOfWeek(selectedWeek), 'MMM d')} - {format(endOfWeek(selectedWeek), 'MMM d, yyyy')}
            </h4>
          </div>

          <button
            onClick={() => setSelectedWeek(addDays(selectedWeek, 7))}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Next Week
          </button>
        </div>

        {/* Schedule Grid */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Day Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Kids Present</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Notes</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {schedules.map((schedule) => {
                const isEditing = editingDay === schedule.date

                return (
                  <tr key={schedule.date} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{format(parseISO(schedule.date), 'EEE, MMM d')}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        schedule.day_type === 'holiday' ? 'bg-purple-100 text-purple-700' :
                        schedule.day_type === 'school' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {schedule.day_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="flex flex-wrap gap-2">
                          {settings.custody_children.map(child => (
                            <label key={child} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={editingKids.includes(child)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEditingKids([...editingKids, child])
                                  } else {
                                    setEditingKids(editingKids.filter(k => k !== child))
                                  }
                                }}
                                className="mr-1"
                              />
                              <span className="capitalize">{child}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(schedule.kids_present || []).map(kid => (
                            <span key={kid} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full capitalize">
                              {kid}
                            </span>
                          ))}
                          {(!schedule.kids_present || schedule.kids_present.length === 0) && (
                            <span className="text-gray-400">None</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {schedule.notes || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleUpdateSchedule(schedule.date, editingKids)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingDay(null)
                              setEditingKids([])
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingDay(schedule.date)
                            setEditingKids(schedule.kids_present || [])
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderModificationsTab = () => (
    <div>
      <h3 className="text-lg font-semibold mb-4">Custody Modifications</h3>

      {modifications.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No custody modifications for this period</p>
        </div>
      ) : (
        <div className="space-y-4">
          {modifications.map(mod => (
            <div key={mod.id} className="bg-white border rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium">
                    {format(parseISO(mod.date), 'MMMM d, yyyy')} - <span className="capitalize">{mod.child_name}</span>
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    From {mod.original_parent} to {mod.actual_parent}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Reason: {mod.reason}
                  </p>
                  {mod.notes && (
                    <p className="text-sm text-gray-500 mt-1">
                      Notes: {mod.notes}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Requested by {mod.created_by} on {format(parseISO(mod.created_at), 'MMM d, yyyy')}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {mod.agreed_by_both ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                      Approved
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => handleApproveModification(mod.id, true)}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleApproveModification(mod.id, false)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderSettingsTab = () => (
    <div>
      <h3 className="text-lg font-semibold mb-4">Custody Settings</h3>

      <div className="space-y-6">
        {/* Enable/Disable */}
        <div className="bg-white border rounded-lg p-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.custody_enabled}
              onChange={(e) => setSettings({ ...settings, custody_enabled: e.target.checked })}
              className="mr-3"
            />
            <div>
              <span className="font-medium">Enable Custody Features</span>
              <p className="text-sm text-gray-600">Show custody information throughout the system</p>
            </div>
          </label>
        </div>

        {/* Parents Configuration */}
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-medium mb-1">Parent Names</h4>
          <p className="text-sm text-gray-600 mb-3">
            List the household parent first. Kids marked present in the schedule are with them.
          </p>
          <div className="space-y-2">
            {settings.custody_parents.map((parent, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={parent}
                  placeholder={index === 0 ? 'Household parent' : index === 1 ? 'Other parent' : 'Parent name'}
                  onChange={(e) => {
                    const newParents = [...settings.custody_parents]
                    newParents[index] = e.target.value
                    setSettings({ ...settings, custody_parents: newParents })
                  }}
                  className="flex-1 px-3 py-2 border rounded-lg"
                />
                {settings.custody_parents.length > 2 && (
                  <button
                    onClick={() => {
                      setSettings({
                        ...settings,
                        custody_parents: settings.custody_parents.filter((_, i) => i !== index)
                      })
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => {
                setSettings({
                  ...settings,
                  custody_parents: [...settings.custody_parents, '']
                })
              }}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Parent
            </button>
          </div>
        </div>

        {/* Children Configuration */}
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-medium mb-3">Children</h4>
          <div className="space-y-2">
            {settings.custody_children.map((child, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={child}
                  onChange={(e) => {
                    const newChildren = [...settings.custody_children]
                    newChildren[index] = e.target.value
                    setSettings({ ...settings, custody_children: newChildren })
                  }}
                  className="flex-1 px-3 py-2 border rounded-lg"
                />
                <button
                  onClick={() => {
                    setSettings({
                      ...settings,
                      custody_children: settings.custody_children.filter((_, i) => i !== index)
                    })
                  }}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                setSettings({
                  ...settings,
                  custody_children: [...settings.custody_children, '']
                })
              }}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Child
            </button>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSaveSettings}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save Settings
        </button>
      </div>
    </div>
  )

  const renderImportTab = () => (
    <div>
      <h3 className="text-lg font-semibold mb-4">Import Court Order</h3>

      <div className="bg-white border rounded-lg p-6">
        <div className="mb-6">
          <h4 className="font-medium mb-2">Upload Court Order PDF</h4>
          <p className="text-sm text-gray-600 mb-4">
            Upload a court order document to automatically extract custody schedule information.
            The system will parse holiday schedules, weekend patterns, and special provisions.
          </p>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />

            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="hidden"
              id="court-order-upload"
            />

            <label
              htmlFor="court-order-upload"
              className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              <Upload className="w-4 h-4" />
              Select PDF File
            </label>

            {uploadFile && (
              <div className="mt-4">
                <p className="text-sm text-gray-700">Selected: {uploadFile.name}</p>
                <p className="text-xs text-gray-500">Size: {(uploadFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            )}
          </div>

          {uploadFile && (
            <button
              onClick={handleFileUpload}
              className="mt-4 w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-4 h-4" />
              Process Court Order
            </button>
          )}
        </div>

        <div className="pt-6 border-t">
          <h4 className="font-medium mb-3">What will be imported:</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
              <span>Holiday custody schedule (Thanksgiving, Christmas, Spring Break, etc.)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
              <span>Weekend rotation patterns</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
              <span>Special provisions and exceptions</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
              <span>School break arrangements</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )

  return (
    <div className="max-w-7xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Custody Management</h2>
        <p className="text-gray-600">Configure and manage custody schedules and modifications</p>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('schedule')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'schedule'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Schedule
            </div>
          </button>

          <button
            onClick={() => setActiveTab('modifications')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'modifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />
              Modifications
            </div>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </div>
          </button>

          <button
            onClick={() => setActiveTab('import')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'import'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Import
            </div>
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="bg-gray-50 rounded-lg p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Error loading custody data</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
                <button
                  onClick={() => {
                    loadCustodyData()
                    loadSettings()
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'schedule' && renderScheduleTab()}
            {activeTab === 'modifications' && renderModificationsTab()}
            {activeTab === 'settings' && renderSettingsTab()}
            {activeTab === 'import' && renderImportTab()}
          </>
        )}
      </div>
    </div>
  )
}

export default CustodyManagement